import * as React from "react";
import TableHeaderSection from "../../tableHeaderSection/TableHeaderSection";
import CheckboxDataInput from "../../checkboxDataInput/CheckboxDataInput";
import { ComponentsHelper } from "../../../../core/services/ComponentHelper";
import { IRepertoireField } from "../../../types/IRepertoireField";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { isTableAction } from "../../../types/ITableAction";
import {
    COMPONENTS_KEY,
    CONTRIBUTORS_KEY,
    DISTRIBUTION_POOLS_KEY,
    EDITABLE_FIELDS_GRID,
    EMPTY_STRING_VALUE,
    IP_LICENSING_REPRESENTATION_KEY,
    IP_NAMES_KEY,
    MATCHING_REPERTOIRE_PRODUCTS_KEY,
    OTHER_KEY,
    PRODUCT_CONTRIBUTORS_KEY,
    USAGE_ROWS_SECTION_KEY,
} from "../../../Consts";
import {
    DRAFT_SET_LISTS_WORK_STATE_KEY,
    SET_LISTS_WORK_SEARCHVIEW_STATE_KEY,
} from "../../../../membersportal/Consts";

interface IGetSectionDataParams {
    section: IRepertoireField;
    usageType?: string;
    showRowName?: boolean;
    totalPerfPercentage: string;
    totalMechPercentage: string;
    changeDataForLivePerformance: (data: string) => string;
}

interface IRenderHeaderCheckboxParams {
    sectionName: string;
    isBulkCheckboxActive?: boolean;
    allowMultiSelectChbk?: boolean;
    changeTableDataForMultiSelect: (value: any, fieldName: string) => void;
}

interface IGetTableHeaderStyleParams {
    section: IRepertoireField;
    isProductMaintenanceAccordionCore?: boolean;
}

const getTotalPercentage = (
    tableContents: any[] | null | undefined,
    fieldName: string
): string => {
    const total = (tableContents ?? []).reduce(
        (sum, item) => sum + Number(item?.[fieldName]?.value ?? 0),
        0
    );

    return total.toFixed(2);
};

export interface IGetHeaderSectionsParams {
    dataGridTableData: IRepertoireComponentDataItem;
    componentInstance: string;
    showRowNumber: boolean;
    allowMultiSelectChbk?: boolean;
    isBulkCheckboxActive?: boolean;
    usageType?: string;
    tableContents?: any[];
    activeSetType?: string;
    checkableColumns?: string[];
    isRowSelectionEnabled?: boolean;
    activeSortHeaderSection?: string;
    checkedColumns: string[];
    ascending: boolean;
    showRowName?: boolean;
    isProductMaintenanceAccordionCore?: boolean;
    sortTableByColumn: (section: string) => void;
    handleHeaderCheckboxChange: (fieldName: string, isChecked: boolean) => void;
    changeDataForLivePerformance: (data: string) => string;
    changeTableDataForMultiSelect: (value: any, fieldName: string) => void;
}

const getSectionData = ({
    section,
    usageType,
    showRowName,
    totalPerfPercentage,
    totalMechPercentage,
    changeDataForLivePerformance,
}: IGetSectionDataParams): React.ReactNode => {
    if (usageType === "SET LIST") {
        return changeDataForLivePerformance(section.data);
    }

    if (section.componentInstance === CONTRIBUTORS_KEY && !showRowName) {
        switch (section.name) {
            case "perfPercentage":
                return (
                    <div className="tableHeaderSectionContainer">
                        {section.data}
                        <div className="tableHeaderSectionValue">{totalPerfPercentage}</div>
                    </div>
                );
            case "mechPercentage":
                return (
                    <div className="tableHeaderSectionContainer">
                        {section.data}
                        <div className="tableHeaderSectionValue">{totalMechPercentage}</div>
                    </div>
                );
            default:
                return section.data;
        }
    }

    return section.data;
};

const renderHeaderCheckbox = ({
    sectionName,
    isBulkCheckboxActive,
    allowMultiSelectChbk,
    changeTableDataForMultiSelect,
}: IRenderHeaderCheckboxParams): JSX.Element => {
    return (
        <div className="headerCell" data-testid="header-checkbox">
            <CheckboxDataInput
                label=""
                fieldName=""
                value={isBulkCheckboxActive}
                changeData={(value) => changeTableDataForMultiSelect(value, sectionName)}
                readonly={false}
                isHidden={!allowMultiSelectChbk}
            />
        </div>
    );
};

const getTableHeaderStyle = ({
    section,
    isProductMaintenanceAccordionCore,
}: IGetTableHeaderStyleParams): string => {
    if (section.componentInstance === MATCHING_REPERTOIRE_PRODUCTS_KEY) {
        switch (section.data) {
            case "Title":
                return "col-lg-4 col-md-4 col-xs-4";
            case "Number":
                return "col-lg-2 col-md-2 col-xs-2";
            case "isDefinite":
                return "col-lg-1 col-md-1 col-xs-1";
            case "merge":
                return "col-lg-3 col-md-3 col-xs-3";
            case "remove_action":
                return "col-lg-2 col-md-2 col-xs-2";
            default:
                return EMPTY_STRING_VALUE;
        }
    }

    if (section.componentInstance === COMPONENTS_KEY) {
        if (section.name === "title") {
            return "col-lg-6 col-md-6 col-xs-6";
        }

        if (section.name === "number" || section.name === "writer") {
            return "col-lg-3 col-md-3 col-xs-3";
        }

        return EMPTY_STRING_VALUE;
    }

    if (
        section.componentInstance === PRODUCT_CONTRIBUTORS_KEY &&
        !isProductMaintenanceAccordionCore &&
        (section.name === "avRipRoleCode" || section.name === "name")
    ) {
        return "col-lg-4 col-md-4 col-sm-4";
    }

    return EMPTY_STRING_VALUE;
};

export const getHeaderSections = ({
    dataGridTableData,
    componentInstance,
    showRowNumber,
    allowMultiSelectChbk,
    isBulkCheckboxActive,
    usageType,
    tableContents,
    activeSetType,
    checkableColumns = [],
    isRowSelectionEnabled = false,
    activeSortHeaderSection,
    checkedColumns,
    ascending,
    showRowName,
    isProductMaintenanceAccordionCore,
    sortTableByColumn,
    handleHeaderCheckboxChange,
    changeDataForLivePerformance,
    changeTableDataForMultiSelect,
}: IGetHeaderSectionsParams): JSX.Element[] => {
    let currentComponentInstance = componentInstance;

    if (componentInstance === SET_LISTS_WORK_SEARCHVIEW_STATE_KEY) {
        currentComponentInstance = DRAFT_SET_LISTS_WORK_STATE_KEY;
    }

    const editableGridFields =
        currentComponentInstance === EDITABLE_FIELDS_GRID
            ? "td col-lg-6 col-md-6 col-xs-6"
            : "td";

    if (!dataGridTableData || !dataGridTableData.fields) {
        return [];
    }

    let filteredFields: IRepertoireField[] = dataGridTableData.fields.filter(
        field => field.componentInstance === currentComponentInstance
    );

    const shouldFilterHiddenFields =
        currentComponentInstance === CONTRIBUTORS_KEY ||
        currentComponentInstance === IP_NAMES_KEY ||
        currentComponentInstance === USAGE_ROWS_SECTION_KEY;

    if (shouldFilterHiddenFields) {
        if (activeSetType != OTHER_KEY) {
            filteredFields = filteredFields.filter(
                x =>
                    !x.hidden &&
                    x.name != "includeTerritories" &&
                    x.name != "excludeTerritories"
            );
        }

        filteredFields = filteredFields.filter(x => !x.hidden);
    }

    let fieldsToRender = filteredFields;

    const shouldOrderFields =
        (currentComponentInstance === CONTRIBUTORS_KEY ||
            currentComponentInstance === USAGE_ROWS_SECTION_KEY) &&
        filteredFields.length > 0;

    if (shouldOrderFields) {
        fieldsToRender = ComponentsHelper.orderFieldsBySpecificField(
            filteredFields,
            "order"
        );
    }

    const totalPerfPercentage = getTotalPercentage(tableContents, "perfPercentage");
    const totalMechPercentage = getTotalPercentage(tableContents, "mechPercentage");

    const headerSections = fieldsToRender.map((section: IRepertoireField, index: number) => {
        const shouldRenderSelectionCheckbox =
            section.name === "selected" ||
            (currentComponentInstance === DISTRIBUTION_POOLS_KEY &&
                section.name === "selected" &&
                allowMultiSelectChbk === true);

        const createHeaderCheckbox = () =>
            renderHeaderCheckbox({
                sectionName: section.name,
                isBulkCheckboxActive,
                allowMultiSelectChbk,
                changeTableDataForMultiSelect,
            });

        return (
            <td
                key={section.name}
                className={`${editableGridFields} ${getTableHeaderStyle({
                    section,
                    isProductMaintenanceAccordionCore,
                })}`}
            >
                {isRowSelectionEnabled && index === 0 && createHeaderCheckbox()}

                {shouldRenderSelectionCheckbox ? (
                    createHeaderCheckbox()
                ) : (
                    <TableHeaderSection
                        section={getSectionData({
                            section,
                            usageType,
                            showRowName,
                            totalPerfPercentage,
                            totalMechPercentage,
                            changeDataForLivePerformance,
                        })}
                        fieldName={section.name}
                        isSortingActive={section.name === activeSortHeaderSection}
                        ascending={ascending}
                        onClickHeaderSection={sortTableByColumn}
                        sortable={
                            currentComponentInstance === EDITABLE_FIELDS_GRID
                                ? false
                                : !isTableAction(section.fieldType)
                        }
                        checkable={checkableColumns.includes(section.name)}
                        isChecked={
                            checkableColumns.includes(section.name)
                                ? checkedColumns.includes(section.name)
                                : undefined
                        }
                        onCheckboxChange={
                            checkableColumns.includes(section.name)
                                ? handleHeaderCheckboxChange
                                : undefined
                        }
                    />
                )}
            </td>
        );
    });

    if (
        showRowNumber &&
        currentComponentInstance !== IP_LICENSING_REPRESENTATION_KEY
    ) {
        return [<td key="rowNumber" title="Row Number"></td>, ...headerSections];
    }

    return headerSections;
};